/* globals ClientRect */
import { RefObject } from 'react';
import isClient from './environment';

export const NAV_ALLOWANCE_PIXELS = 60;
export const ANIMATED_SCROLL_DURATION = 350; // ms
export const ANIMATED_SCROLL_EASING_FUNCTION = 'easeInOutCubic';
export const observerApiErrorMessage = 'Could not use the Intersection Observer API';

export interface ObserverOptions {
    root?: Element | null;
    rootMargin?: string;
    threshold?: number | Array<number>;
}

const defaultObserverOptions: ObserverOptions = {
    root: null,
    rootMargin: '0px 80px 200px 0px',
};

export interface ObserverEntryInterface<T> {
    isIntersecting: boolean;
    target: T;
}

interface ScrollIntoViewParams {
    container: HTMLElement;
    child: HTMLElement;
    margin: number;
    setScrollPosition: (scrollX: number) => void;
}

let boundingRectCache: ExtendedHtmlElement[] = [];

interface ExtendedHtmlElement extends HTMLElement {
    cachedBoundingRect?: ClientRect | DOMRect;
}

function getCachedBoundingClientRect(element: HTMLElement) {
    if (!isClient()) {
        return element.getBoundingClientRect();
    }
    const el = element as ExtendedHtmlElement;
    if (el.cachedBoundingRect) {
        return el.cachedBoundingRect;
    }

    el.cachedBoundingRect = el.getBoundingClientRect();
    boundingRectCache.push(el);
    return el.cachedBoundingRect;
}

function cleanBoundingRectCache() {
    boundingRectCache.forEach(el => {
        // eslint-disable-next-line no-param-reassign
        el.cachedBoundingRect = undefined;
    });
    boundingRectCache = [];
}

function scrollIntoViewHorizontally({ container, child, margin, setScrollPosition }: ScrollIntoViewParams): void {
    const childRect = getCachedBoundingClientRect(child);
    const containerRect = getCachedBoundingClientRect(container);

    const notFitFromLeft = childRect.left < 0;
    const notFitFromRight = childRect.right > containerRect.right;

    if (notFitFromLeft) {
        setScrollPosition(child.offsetLeft - margin);
        cleanBoundingRectCache();
    } else if (notFitFromRight) {
        setScrollPosition(child.offsetLeft - containerRect.right + childRect.width + margin);
        cleanBoundingRectCache();
    }
}

export const scrollToTop = () => {
    if (isClient()) {
        window.scrollTo(0, 0);
    }
};

export const scrollToElement = (element: HTMLElement) => {
    if (element) {
        window.scroll(0, element.offsetTop + NAV_ALLOWANCE_PIXELS);
    }
};

export const safeRequestIdleCallback = <T extends Function>(callback: T) => {
    if (typeof window !== 'undefined' && window.requestIdleCallback) {
        window.requestIdleCallback(() => {
            callback();
        });
    } else {
        callback();
    }
};
export type IntersectedCallback = (error?: string, target?: Element) => void;

export const observeElement = <E extends HTMLElement>(
    element: RefObject<E>,
    callback: IntersectedCallback,
    options?: ObserverOptions
) => {
    let intersectionObserver;

    try {
        const onObserverCallback = <T extends Element>(
            entries: ObserverEntryInterface<T>[],
            observer: IntersectionObserver
        ): void => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    callback(undefined, entry.target);
                    observer.disconnect();
                }
            });
        };

        const handleObserve = (observer: IntersectionObserver) => {
            if (element && element.current) {
                observer.observe(element.current);
            }
        };

        if (typeof window !== 'undefined' && window.IntersectionObserver) {
            const newOptions = options ? { ...defaultObserverOptions, ...options } : defaultObserverOptions;
            intersectionObserver = new IntersectionObserver(onObserverCallback, newOptions);
            handleObserve(intersectionObserver);
        } else {
            callback();
        }
    } catch (error) {
        callback(observerApiErrorMessage);
    }

    return intersectionObserver;
};

export type ObserveElementParameters = Parameters<typeof observeElement>;
export type ElementObserver = ReturnType<typeof observeElement>;

export default { scrollIntoViewHorizontally, scrollToTop };
